import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import i18next from 'i18next';
import { NO_ROUTE_ID, RoutesPlannerConfiguration, RoutesPlannerTask } from '@bringg-frontend/routes-planner';
import { EtaResponseBreakItem, EtaResponseTaskItem, EtaResponseType } from '@bringg-frontend/eta-calculator';
import { Task } from '@bringg/types';
import { dateUtils, getDropOffWayPoint } from '@bringg-frontend/utils';

import { CLUSTER_MODE_TYPES, OPTIMIZATION_UNASSIGN_REASON } from '../consts';
import { OptimizationData } from '../routes-planner-modal-types';

export const addOptimizationDataToTaskData = (task: Task, optimizationData: EtaResponseTaskItem, runId: number) => {
	const foundTask = { ...task };
	foundTask.run_id = runId;
	foundTask.user_id = optimizationData.user_id;
	foundTask.priority = optimizationData.priority;
	foundTask.vehicle_id = +optimizationData.vehicle_id;

	const dropOffWayPoint = getDropOffWayPoint(foundTask);

	dropOffWayPoint.scheduled_at = dateUtils.unixToISOString(optimizationData.eta);
	dropOffWayPoint.eta = dateUtils.unixToISOString(optimizationData.eta);
	dropOffWayPoint.etl = dateUtils.unixToISOString(optimizationData.etl);
	if (optimizationData.depot_etl) {
		dropOffWayPoint.has_to_leave_by = dateUtils.unixToISOString(optimizationData.depot_etl);
	}
	foundTask.way_points = [dropOffWayPoint];
	return foundTask;
};

export const handleUnassignTask = (task: Task, item?: OptimizationData) => {
	const foundTask: RoutesPlannerTask = { ...task };
	foundTask.run_id = NO_ROUTE_ID;
	foundTask.user_id = null;
	if (item) {
		const unassignedReason = item
			? OPTIMIZATION_UNASSIGN_REASON[item.reason_code]
			: OPTIMIZATION_UNASSIGN_REASON[1];
		foundTask.reason = i18next.t(`ROUTE_OPTIMIZATION_UNASSIGNED_REASON.${unassignedReason}`);
	}
	const dropOffWayPoint = getDropOffWayPoint(foundTask);
	foundTask.way_points = [dropOffWayPoint];

	return foundTask;
};

export const transformActualBreaksData = (OptimizationBreak: EtaResponseBreakItem, runId: number) => {
	const brake = { ...OptimizationBreak };
	brake.run_id = runId;
	brake.estimated_start_time = dateUtils.getTimeFromUnixTime(OptimizationBreak.estimated_start_time);
	brake.estimated_end_time = dateUtils.getTimeFromUnixTime(OptimizationBreak.estimated_end_time);
	brake.scheduled_start_time = dateUtils.getTimeFromUnixTime(OptimizationBreak.scheduled_start_time);
	brake.scheduled_end_time = dateUtils.getTimeFromUnixTime(OptimizationBreak.scheduled_end_time);

	return brake;
};

export const updateEtaResponse = ({
	selectedTask,
	runIdByRouteIndex,
	optimizationResult,
	selectedTasksObject,
	breaks
}) => {
	const updateDirections = [];
	const updateBreaks = { ...breaks };
	const routeIdx = optimizationResult.route_idx;
	const updateSelectedTasks = { ...selectedTasksObject };
	const runId = runIdByRouteIndex[routeIdx] || routeIdx;
	switch (optimizationResult.type) {
		case EtaResponseType.Task: {
			updateSelectedTasks[optimizationResult.task_id] = addOptimizationDataToTaskData(
				selectedTask,
				optimizationResult,
				runId
			);
			break;
		}
		case EtaResponseType.RouteInfo: {
			if (runId !== routeIdx) {
				Object.assign(optimizationResult, { run_id: runId });
			}
			break;
		}
		case EtaResponseType.UnassignedTask: {
			updateSelectedTasks[optimizationResult.id] = handleUnassignTask(selectedTask, optimizationResult);

			break;
		}
		case EtaResponseType.ActualBreak:
		case EtaResponseType.PreassignedBreak: {
			if (updateBreaks[runId]) {
				updateBreaks[runId].push(transformActualBreaksData(optimizationResult, runId));
			} else {
				updateBreaks[runId] = [transformActualBreaksData(optimizationResult, runId)];
			}
			break;
		}
		case EtaResponseType.Directions: {
			updateDirections.push({ run_id: runId, directions: optimizationResult.directions });
			break;
		}
		default:
			break;
	}
	return { updateDirections, updateBreaks, updateSelectedTasks };
};

export const getTaskUsersType = (run, tasks, plannedRoutes, userTypes, employees) => {
	const firstTask = tasks[0];
	const rowRouteName = firstTask.route_name;

	if (firstTask.user) {
		return firstTask.user.userType;
	}
	const plannedRouteWithRouteName = plannedRoutes?.find(plannedRoute => plannedRoute.id === run?.planned_route_id);
	if (rowRouteName && run && plannedRouteWithRouteName) {
		return userTypes.find(userType => userType.id === plannedRouteWithRouteName.user_type_id);
	}
	if (run) {
		const user = employees.find(employee => employee.id === run.user_id);

		return user && userTypes.find(type => type.id === user?.user_type_id);
	}
	return undefined;
};

export const getRouteInfoByIndex = (optimizationData: OptimizationData[]) => {
	const routeInfoByIndex = {};
	optimizationData &&
		optimizationData.forEach(data => {
			if (data.type === EtaResponseType.RouteInfo) {
				routeInfoByIndex[data.route_idx] = data;
			}
		});

	return routeInfoByIndex;
};

export const getRunIdByRouteIndex = (optimizationData: OptimizationData[]) => {
	return optimizationData.reduce((all, current) => {
		if (current.run_id) {
			all[current.route_idx] = current.run_id;
		}
		return all;
	}, {});
};

const processValidationResult = (task, validations) => {
	let errSum = 0;
	let canAssign = true;
	validations.forEach(checker => {
		if (checker.validation(task)) {
			canAssign = false;
			errSum = errSum || checker.errCode;
		}
	});
	return { canAssign, errSum };
};

export const validateTasksFilter = (tasks, validations) => {
	const assignableTasks = [];
	let errSumTotal = 0;
	tasks.forEach(task => {
		const { canAssign, errSum } = processValidationResult(task, validations);
		errSumTotal = errSumTotal || errSum;
		if (canAssign) {
			assignableTasks.push(task);
		}
	});
	return { canAssign: assignableTasks.length > 0, assignableTasks, errSum: errSumTotal, errMessage: '' };
};

export const isClusterModeEnabled = async () => {
	const userConfiguration = await getRootEnv().dashboardSdk.sdk.userConfigurations.get();
	return (
		userConfiguration.cluster_mode &&
		[CLUSTER_MODE_TYPES.INVENTORY_PICKUP, CLUSTER_MODE_TYPES.INVENTORY_PICKUP_DROPOFF].includes(
			userConfiguration.cluster_mode_type
		)
	);
};

export const updateRoutesPlannerConfig = async (config: RoutesPlannerConfiguration) => {
	const routesPlannerConfig = { ...config };

	routesPlannerConfig.isClusterMode = await isClusterModeEnabled();
	routesPlannerConfig.endOfWeekDay = config.endOfWeekDay;
	routesPlannerConfig.ignoreQuantityInLoadCalculation = config.ignoreQuantityInLoadCalculation;

	return routesPlannerConfig;
};
